// @flow

import * as React from 'react';

import { connect } from 'react-redux';

import type {
  CartConfiguration,
  ProductKind,
  ProductType,
  Process,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';

import {
  selectConfigurationFactory,
  upsertConfiguration,
} from 'src/modules/configurations';
import { selectShopCurrent } from 'src/modules/organization/selectors';

import type { FullState, Dispatch } from 'src/types';

import { AutoRenewBlock } from '../../../../AutoRenewBlock';

type OwnProps = {
  kind: ProductKind,
  productType: ProductType,
  process: Process,
};

type StateProps = {
  orgAutorenew: boolean,
  configuration: ?CartConfiguration,
};

type DispatchProps = {
  dispatch: Dispatch,
};

type Props = {
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
};

function Autorenew({
  dispatch,
  kind,
  orgAutorenew,
  configuration,
  productType,
  process,
}: Props) {
  const { __ } = useTranslator();
  const {
    // $FlowFixMe[incompatible-type]
    payload: { autorenew: configAutorenew = null, ...restPayload } = {},
    // $FlowFixMe[incompatible-type]
    id: configId = null,
  } = configuration || {};

  const updateConfigAutorenew = (value: ?boolean) =>
    upsertConfiguration({
      id: configId,
      product_type: productType,
      process,
      // $FlowExpectedError[cannot-spread-inexact] - payload definition is inexact
      payload: { autorenew: value, ...restPayload },
    });

  const [checked, setChecked] = React.useState<boolean>(
    kind === 'MailboxStandAloneCreate' ||
      kind === 'Mailboxv2Create' ||
      !!configAutorenew,
  );

  React.useEffect(() => {
    // Configuration's `autorenew` setting is redundant.
    // Clear it as a best-effort towards data-cleanliness.
    if (orgAutorenew && configAutorenew) {
      dispatch(updateConfigAutorenew(null)).catch(() => {
        // pass
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgAutorenew, configAutorenew]);

  const onChange = async (val: boolean) => {
    setChecked(val);
    try {
      await dispatch(updateConfigAutorenew(val));
    } catch (err) {
      setChecked(!val);
      dispatch(
        notifyDanger({
          message: __(
            'Unable to update automatic renewal status for these products at this time.',
          ),
        }),
      );
      throw err;
    }
  };

  const autoRenewBlockProps = orgAutorenew
    ? {
        popoverText: __(
          'This option has been activated on the organization settings.' +
            ' Each new product is automatically renewed when expiring.',
        ),
      }
    : {
        onChange,
      };

  return (
    <AutoRenewBlock
      name={kind}
      id={`autorenewStatus-${kind}`}
      checked={checked}
      {...autoRenewBlockProps}
    />
  );
}

const selectOrgAutorenew = (state: FullState): boolean => {
  const org = selectShopCurrent(state, false);
  return !!org?.autorenew;
};

function makeMapStateToProps(_: FullState, { productType, process }: OwnProps) {
  const selectConfiguration = selectConfigurationFactory({
    productType,
    process,
  });
  return function mapStateToProps(state: FullState) {
    return {
      orgAutorenew: selectOrgAutorenew(state),
      configuration: selectConfiguration(state),
    };
  };
}

// $FlowFixMe[not-an-object]
// $FlowFixMe[underconstrained-implicit-instantiation]
export default (connect(makeMapStateToProps)(Autorenew): Class<
  // $FlowFixMe[cannot-resolve-name]
  ConnectedComponent<OwnProps, (_0: Props) => React.Node>,
> &
  (Props => React.Node));
